import React, {
  createContext,
  Dispatch,
  MutableRefObject,
  ReactNode,
  SetStateAction,
  useCallback,
  useEffect,
  useState
} from 'react';
import classes from './ButtonMenu.module.scss';
import { IconsEnum } from '../../../constants/icons';
import { Button } from '../Button';
import { ListItemContainer } from '../../containers/ListItemContainer';
import classNames from 'classnames';
import { ContainerListItem } from '../ListItem/ListItem.view';
import { ListItem } from '../ListItem';
import { ButtonColorsEnum } from '../../../constants/button-colors';
import { SquareButtonIconsEnum } from '../../../constants/square-button-icons';
import { ButtonViewProps, IconType } from '../Button/Button.view';
import { SquareButton } from '../SquareButton';
import { Tooltip } from '../Tooltip';
import { idGenerator } from '../../../helpers/idGenerator';
import { LIST_CONTAINER_VERTICAL_PADDING } from '../../../constants/styleConstants';
import { OutsideClickWrapper } from '../OutsideClickWrapper';
import { DropdownWrapper } from '../../containers/DropdownWrapper';
import { useForwardedRefTransformer } from '../../../hooks/useForwardedRef';
import scssVars from '../../../assets/scss/exportedVars.module.scss';

export type ContainerListItemExtended = ContainerListItem & {
  disabled: boolean;
  disabledTooltipText: string;
};

export type ButtonTypeProps =
  | {
      isSquareButton: true;
      buttonType: 'transparent' | 'white';
      icon?: SquareButtonIconsEnum;
      iconSide?: never;
      size?: never;
      variant?: never;
      label?: never;
      expanded?: never;
    }
  | {
      isSquareButton: false;
      buttonType?: never;
      icon: IconsEnum;
      iconSide: IconType['side'];
      size: ButtonViewProps['size'];
      variant: ButtonViewProps['variant'];
      label?: string;
      expanded?: never;
    }
  | {
      isSquareButton: false;
      buttonType?: never;
      icon: never;
      iconSide: never;
      size: ButtonViewProps['size'];
      variant: ButtonViewProps['variant'];
      label: string;
      expanded?: true;
    };

export type ButtonContentType =
  | {
      listItems: Array<ContainerListItem | ContainerListItemExtended>;
      children?: never;
    }
  | {
      children: ReactNode;
      listItems?: never;
    };

export type ButtonMenuViewProps = {
  disabled?: boolean;
  elementType?: 'a' | 'button';
  dropdownMaximize?: boolean;
  isSquareButton: boolean;
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  variantOnOpen?: ButtonViewProps['variant'];
  color?: ButtonColorsEnum;
  side: 'left' | 'right';
  onMenuShowEventCallback?: (open: boolean) => void;
  handleClickOutsideComponent: VoidFunction;
  portalElementRef: MutableRefObject<HTMLDivElement | null>;
} & ButtonTypeProps &
  ButtonContentType;

export type ButtonMenuContextType = {
  openHandler: (open: boolean) => void;
};

export const ButtonMenuContext = createContext<ButtonMenuContextType>({
  openHandler: () => {
    //...
  }
});

export const ButtonMenuView = React.forwardRef<HTMLDivElement, ButtonMenuViewProps>(
  (props: ButtonMenuViewProps, ref): JSX.Element => {
    const { setOpen, onMenuShowEventCallback, portalElementRef } = props;
    const mutableRef = useForwardedRefTransformer(ref);
    const [dropdownHeight, setDropdownHeight] = useState<number | undefined>(undefined);

    const openMenuHandler = useCallback(
      (open: boolean) => {
        setOpen(open);

        // If a callback is provided, execute it
        if (onMenuShowEventCallback) {
          onMenuShowEventCallback(open);
        }
      },
      [onMenuShowEventCallback, setOpen]
    );

    const classesType = props.isSquareButton ? 'SquareButtonMenu' : 'ButtonMenu';
    const containerClasses = classNames(classes[classesType]);
    const buttonWrapperClasses = props.isSquareButton
      ? classNames(classes.SquareButtonMenuWrapper)
      : classNames(classes.ButtonMenuWrapper);

    const variant = props.open && !props.isSquareButton && props.variantOnOpen ? props.variantOnOpen : props.variant;

    const buttonEl: JSX.Element = props.isSquareButton ? (
      <SquareButton
        {...(props.disabled ? { disabled: true } : {})}
        {...(props.elementType ? { elementType: props.elementType } : {})}
        type={props.buttonType}
        icon={props.icon || SquareButtonIconsEnum.dots}
        active={props.open}
        action={() => openMenuHandler(!props.open)}
      />
    ) : (
      <Button
        {...(props.disabled ? { disabled: true } : {})}
        type={props.elementType || 'button'}
        variant={variant || props.variant}
        active={props.open}
        action={() => openMenuHandler(!props.open)}
        size={props.size || 'medium'}
        color={props.color || ButtonColorsEnum.primary}
        expanded={props.expanded || undefined}
        icon={props.icon ? { side: props.iconSide || 'center', value: props.icon || IconsEnum.threeDot } : undefined}>
        {props.label}
      </Button>
    );

    const dropdownContent = props.listItems ? (
      <ListItemContainer type={'div'} expanded={props.open}>
        {props.listItems.map((item, index) => {
          // Typeguard for the two different SelectItem types
          const uniqId = idGenerator();
          const hasDisabledProp = 'disabled' in item;
          const hasTooltipProp = 'disabledTooltipText' in item;

          return (
            <div key={index}>
              <div id={`item-${uniqId}`} className={classes.ListItemWrapper}>
                <ListItem
                  listItemLinkType={props.elementType}
                  type={'div'}
                  disabled={hasDisabledProp ? !!item.disabled : false}
                  variation={'regular'}
                  unsetWidth={hasDisabledProp ? item.disabled : false}
                  expanded={true}
                  selected={false}
                  key={index}
                  action={() => {
                    item.action();
                    props.setOpen(false);
                  }}>
                  {item.label}
                </ListItem>
                {hasDisabledProp && hasTooltipProp && (
                  <div className={classes.TooltipHelpIconTest}>
                    <Tooltip
                      type={'regular'}
                      position={'bottom'}
                      pointer={true}
                      autoZIndex={false}
                      target={`#item-${uniqId}`}
                      style={{ zIndex: scssVars['z-index-overmodal'] }}>
                      <>{item.disabledTooltipText}</>
                    </Tooltip>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </ListItemContainer>
    ) : (
      <ListItemContainer type={'div'} expanded={props.open} maxHeight={dropdownHeight} isDrawer>
        {props.children}
      </ListItemContainer>
    );

    useEffect(() => {
      // This side effect is used to check if we need to make dropdown maximized (e.g. filters dropdown)
      // if we do, we should calculate the max size from the start point to the bottom of the page
      if (mutableRef.current && props.dropdownMaximize && props.open && portalElementRef.current) {
        const { top } = mutableRef.current.getBoundingClientRect();
        const listRealElement = portalElementRef.current?.children[0] || null;
        const dropdownHeight =
          listRealElement && listRealElement.children.length
            ? Array.from(listRealElement.children).reduce((prev, current) => {
                return prev + current.clientHeight;
              }, 0)
            : 0;
        // const { height: dropdownHeight } = portalElementRef.current.getBoundingClientRect();
        const windowOffset =
          window.innerHeight - mutableRef.current.offsetHeight - top - LIST_CONTAINER_VERTICAL_PADDING * 2;
        const maxHeight = Math.min(dropdownHeight, windowOffset);
        setDropdownHeight(maxHeight);
      }
    }, [props.dropdownMaximize, props.open, mutableRef, portalElementRef]);

    return (
      <ButtonMenuContext.Provider value={{ openHandler: openMenuHandler }}>
        <OutsideClickWrapper
          onClickOutside={props.handleClickOutsideComponent}
          onFocusOutside={props.handleClickOutsideComponent}
          ref={ref}
          className={buttonWrapperClasses}>
          {buttonEl}
          <DropdownWrapper
            ref={portalElementRef}
            side={props.side}
            expanded={props.open}
            portalWrapperRef={mutableRef}
            className={containerClasses}>
            {dropdownContent}
          </DropdownWrapper>
        </OutsideClickWrapper>
      </ButtonMenuContext.Provider>
    );
  }
);

ButtonMenuView.displayName = 'ButtonMenu';
